import React from 'react';
import css from './styles/side-navigation.scss';
import rtsProLogo from '../../assets/images/rts-pro-log-white.svg';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import upgradePlan from '../../assets/images/upgrade-plan-icon.svg';
import leftArrowIcon from '../../assets/images/left-arrow-icon.svg';
import rightArrowIcon from '../../assets/images/right-arrow-icon.svg';
import rtsLogo from '../../assets/images/rts.svg';
import { SideNavigationLinks } from './side-navigation-links';
import { getIsSideNavigationCollapsed, setIsSideNavigationCollapsed } from '../../redux/actions/appActions';
import { hasFactoringPermission } from '../../utils/glassboxUtils';
import type { RootState } from '../../bootstrap';
import Feedback from './feedback';
import { Tooltip } from 'shamrock-clover-ui';
import tmsNavigation from 'tms/navigate';
import { PtSubscriptionModal } from '../proTransportSubscriptions/components/ptSubscriptionModal';
import { useCanViewPTSubscriptions } from '../proTransportSubscriptions/hooks/permissions';

export default function SideNavigation() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isCollapsed = useSelector(getIsSideNavigationCollapsed);
  const location = useLocation();
  const permissions = useSelector((state: RootState) => state.app.permissions);
  const features = useSelector<RootState, string[]>((state) => state.app.features);
  const showGlobalFeedbackButton = features.includes(
    '4143dad9033ef4f13d896d6f41c1595faa7e5225fb0d78c29dbacd69dd54464e',
  );

  const canViewPTSubscriptions = useCanViewPTSubscriptions();

  const pathname = location.pathname;
  const isPtActive = pathname.includes('/loads');
  const [showPtSubscriptionModal, setShowPtSubscriptionModal] = React.useState(false);

  const onPtUpgradeClicked = () => {
    if (canViewPTSubscriptions) {
      setShowPtSubscriptionModal(true);
    } else {
      tmsNavigation('/loads/requestupgrade');
    }
  };

  const hasFactoringSelect = hasFactoringPermission(permissions) && pathname.startsWith('/factoring');
  const isPtEnterprise = permissions?.includes('tms.user.enterprise');

  return (
    <div className={classNames(css.container, isCollapsed && css.collapsedContainer)}>
      {isCollapsed ? (
        <img alt="RTS Logo" width={35} height={21} src={rtsLogo} className={css.logo} />
      ) : (
        <img alt="RTS PRO Logo" width={101} height={21} src={rtsProLogo} className={css.logo} />
      )}
      <div className={classNames(css.collapse, hasFactoringSelect && css.collapseWithFactoringSelect)}>
        <button className={css.buttonCollapse} onClick={() => dispatch(setIsSideNavigationCollapsed(!isCollapsed))}>
          <img
            alt={isCollapsed ? 'Expand Side Navigation' : 'Collapse Side Navigation'}
            src={isCollapsed ? rightArrowIcon : leftArrowIcon}
          />
        </button>
        <div className={css.collapseHover}>
          {t(isCollapsed ? 'sideNavigation.expand_side_panel' : 'sideNavigation.collapse_side_panel')}
        </div>
      </div>
      <>
        <SideNavigationLinks />
        <div className={css.bottomContainer}>
          {isPtActive && !isPtEnterprise && (
            <>
              {!isCollapsed && <div className={css.unlock}>{t('sideNavigation.unlock_more_features')}</div>}
              <Tooltip
                title={t('ptUpgradeBanner.upgrade_plan')}
                placement="right"
                style={!isCollapsed ? { display: 'none' } : { zIndex: 9999 }}
              >
                <button
                  className={classNames(css.upgradePlanContainer, isCollapsed && css.upgradePlanCollapsed)}
                  onClick={onPtUpgradeClicked}
                >
                  <img className={css.upgradePlanIcon} src={upgradePlan} />
                  <p className={css.upgradePlanText}>{t('ptUpgradeBanner.upgrade_plan')}</p>
                </button>
              </Tooltip>
            </>
          )}
          {showGlobalFeedbackButton && <Feedback isCollapsed={isCollapsed} />}
        </div>
        {showPtSubscriptionModal && (
          <PtSubscriptionModal
            sideNavWidth={isCollapsed ? 70 : 160}
            open={showPtSubscriptionModal}
            onClose={() => setShowPtSubscriptionModal(false)}
          />
        )}
      </>
    </div>
  );
}
